import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useGetCmsConfigsQuery } from '../../apis/cmsApi/cms.hooks.api';
import { isPdrOrder, isAppointmentOrder } from '../../helpers/mlkActions';
import {
  getCorrectStatus,
  getExpectedServiceDateAndTimeSlot,
} from '../../helpers/order';
import {
  BlockedReason,
  CalculatedOrderStatus,
  Order,
  OrderState,
} from '../../models';
import { useOrder } from '../useOrder';

export const useIsReceptionEnabled = () => {
  const order = useOrder();
  const { data: cmsConfigs } = useGetCmsConfigsQuery(undefined);

  const isReceptionAvailable = useMemo(() => {
    if (order && cmsConfigs) {
      const calculatedOrderStatus = getCorrectStatus(order);
      return getIsReceptionAvailable(
        order,
        calculatedOrderStatus,
        cmsConfigs.featureFlags.isReceptionEnabled
      );
    }
    return false;
  }, [cmsConfigs, order]);

  return isReceptionAvailable;
};

const getIsReceptionAvailable = (
  order: Order,
  calculatedOrderStatus: CalculatedOrderStatus | undefined,
  isServiceEnabled: boolean
) => {
  const isLastAttempt = order.blockedReasons?.includes(BlockedReason.B01);
  const isPdr = isPdrOrder(order);
  const isAppointment = isAppointmentOrder(order);
  const isSuspended = calculatedOrderStatus === CalculatedOrderStatus.Suspended;
  const isReceptionEnabled =
    order.shipperTerritoryConfiguration.uiParams.enableReception;
  const isValidOrderState = order.status === OrderState.ToReschedule;

  const isValidCalculatedStatus =
    calculatedOrderStatus !== undefined &&
    (calculatedOrderStatus === CalculatedOrderStatus.StandBy ||
      calculatedOrderStatus === CalculatedOrderStatus.Scheduled ||
      calculatedOrderStatus === CalculatedOrderStatus.ScheduledReception);

  const expectedDateAndTimeSlot = getExpectedServiceDateAndTimeSlot(order);
  let isSameDayPdr = false;
  if (expectedDateAndTimeSlot) {
    const today = dayjs();
    const hoursDifference = expectedDateAndTimeSlot.to
      .hour(0)
      .minute(0)
      .second(0)
      .diff(today, 'hour');
    isSameDayPdr = hoursDifference <= 0 && !!isPdr;
  }

  let isOrderStatusAllowed = true;
  if (order.receptionRules?.allowedOrderStatuses) {
    if (!order.receptionRules.allowedOrderStatuses.includes(order.mlkStatus)) {
      isOrderStatusAllowed = false;
    }
  }

  return (
    !isLastAttempt &&
    !isAppointment &&
    !isSameDayPdr &&
    !isSuspended &&
    (isValidOrderState || isValidCalculatedStatus) &&
    isServiceEnabled &&
    isReceptionEnabled &&
    isOrderStatusAllowed
  );
};
